import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    OnInit,
    SimpleChanges,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UserProfileService } from '@app/core/_services/user-profile.service'
import { UserProfileInfo, User } from '@app/core/_models'
import { Subject, take, takeUntil } from 'rxjs'
import { AdminService } from '@app/core/_services/admin.service'
import { MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { DialogService } from '@app/core/_services/dialog.service'
import { LoadingIndicatorComponent } from '@app/shared/components/loading-indicator/loading-indicator.component'
import {
    USER_ACTION_BLOCKED,
    USER_ACTION_REMOVE_SAVED,
    USER_ACTION_SAVED,
    USER_ACTION_SNOOZED,
    USER_ACTION_UNBLOCKED,
    USER_ACTION_UNSNOOZED,
} from '@app/user-actions/components/user-actions-menu/user-actions-menu.component'
import {
    BlockStatus,
    UserRelationshipAction,
    UserRelationshipActionTaken,
    UserRelationshipService,
} from '@app/core/_services/user-relationship.service'
import {
    MAX_SET_TIMEOUT_VALUE,
    TimeUtilitiesService,
} from '@app/core/_services/time-utilities.service'
import { RpUserProfileComponent } from '@app/right-panel/components/rp-user-profile/rp-user-profile.component'
import { UserService } from '@app/core/_services/user.service'
import { UserProfileComponent } from '../components/user-profile/user-profile.component'

const ADMIN_PROFILE_URL = '/admin/profile/'

@Component({
    selector: 'fm-profile-container',
    imports: [RpUserProfileComponent, UserProfileComponent],
    templateUrl: './profile-container.component.html',
    styleUrl: './profile-container.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileContainerComponent {
    @Input() profileSlug: string | null = null
    @Input() dynamicPanel: boolean

    // @HostListener('window:focus', ['$event'])
    // onFocus(event: any): void {
    //     this.clearTimer()
    //     this.updateSnoozeStatusIfExpired()
    // }
    // @HostListener('window:blur', ['$event'])
    // onBlur(event: any): void {
    //     this.clearTimer()
    // }
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentUser: User
    public userProfile: UserProfileInfo
    public viewingSelf: boolean = false
    public isFmAdmin: boolean = false
    public isLoadingUserInfo: boolean = true
    private timerId: any

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private userProfileService: UserProfileService,
        private adminService: AdminService,
        private loadingIndicator: MatDialog,
        private dialogService: DialogService,
        private timeUtilitiesService: TimeUtilitiesService,
        private cd: ChangeDetectorRef,
        private userRelationshipService: UserRelationshipService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['profileSlug']) {
            if (!changes['profileSlug'].isFirstChange()) {
                this.ngUnsubscribe.next(null)
                this.ngUnsubscribe.complete()
                this.initializeComponent()
            }
        }
    }

    ngOnInit() {
        this.initializeComponent()
    }

    private initializeComponent(): void {
        this.userRelationshipService.userRelationUpdated
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((actionTaken: UserRelationshipActionTaken) => {
                if (actionTaken.userId == this.userProfile.uid) {
                    if (actionTaken.action === 'save') {
                        this.userProfile = {
                            ...this.userProfile,
                            saved: true,
                        }
                    }
                    if (actionTaken.action === 'unsave') {
                        this.userProfile = {
                            ...this.userProfile,
                            saved: false,
                        }
                    }
                    if (actionTaken.action === 'unsnooze') {
                        this.userProfile = {
                            ...this.userProfile,
                            snoozeStatus: false,
                            snoozeExpiresAt: null,
                        }
                    }

                    if (actionTaken.action === 'snooze') {
                        this.userProfile = {
                            ...this.userProfile,
                            snoozeStatus: true,
                        }
                    }

                    this.cd.markForCheck()
                }
            })

        this.userService.currentUser.pipe(take(1)).subscribe((user) => {
            this.currentUser = user
            this.isFmAdmin = this.currentUser.fmAd || this.currentUser.fmSupport
            this.viewingSelf = this.route.snapshot.params['id'] === this.currentUser.profileUrl
            this.populateProfileView()
        })
    }

    populateProfileView() {
        this.showLoadingIndicator()

        this.userProfileService
            .getUserProfile(this.profileSlug ?? this.route.snapshot.params['id'])
            .subscribe({
                next: (v) => this.receivedUserInfo(v),
                error: (e) => this.receivedError(),
                complete: () => this.removeLoadingIndicator(),
            })
    }

    receivedUserInfo(data: UserProfileInfo) {
        this.userProfile = { ...data }
        this.updateSnoozeStatusIfExpired()
        this.removeLoadingIndicator()
        this.cd.markForCheck()
    }

    receivedError() {
        this.userProfile = new UserProfileInfo()
        this.removeLoadingIndicator()
    }

    handleUserAction(event) {
        if (event.userAction === USER_ACTION_BLOCKED) {
            this.userProfile.status = BlockStatus.blocking
        }
        if (event.userAction === USER_ACTION_UNBLOCKED) {
            this.userProfile.status = BlockStatus.none
        }
        if (event.userAction === USER_ACTION_SNOOZED) {
            this.userProfile.snoozeStatus = true
            this.userProfile.snoozeExpiresAt = event.snoozeExpiresAt
            this.updateSnoozeStatusIfExpired()
        }
        if (event.userAction === USER_ACTION_UNSNOOZED) {
            this.userProfile.snoozeStatus = false
        }
        if (event.userAction === USER_ACTION_SAVED) {
            this.userProfile.saved = true
        }
        if (event.userAction === USER_ACTION_REMOVE_SAVED) {
            this.userProfile.saved = false
        }
    }

    showLoadingIndicator() {
        this.isLoadingUserInfo = true
    }

    removeLoadingIndicator() {
        this.isLoadingUserInfo = false
    }

    suspendUser() {
        this.adminService.suspendUser(this.userProfile.email).subscribe((data) => {
            this.populateProfileView()
        })
    }

    restoreUser() {
        this.adminService.restoreUser(this.userProfile.email).subscribe((data) => {
            this.populateProfileView()
        })
    }

    verifyUser(verify) {
        this.adminService.verifyUser(this.userProfile.email, verify).subscribe((data) => {
            this.populateProfileView()
        })
    }

    cancelSubscription() {
        this.adminService.cancelUserSubscription(this.userProfile.email).subscribe((data) => {
            this.populateProfileView()
        })
    }

    updateEmailAddress(newemailAddress: string) {
        this.adminService.updateEmailAddress(this.userProfile.email, newemailAddress).subscribe(
            (data) => {
                this.populateProfileView()
                this.dialogService.openUpdateEmailAddressResult(data.updateStatus, null)
            },
            (error) => {
                this.dialogService.openUpdateEmailAddressResult(null, error)
            },
        )
    }

    updateSnoozeStatusIfExpired() {
        this.clearTimer()

        if (this.isSnoozedForSetDuration()) {
            const diffInMilliSeconds = this.timeUtilitiesService.getDurationBetweenNowAndISOString(
                this.userProfile.snoozeExpiresAt,
                'milliseconds',
            )

            if (diffInMilliSeconds > 0) {
                if (diffInMilliSeconds < MAX_SET_TIMEOUT_VALUE) {
                    this.timerId = setTimeout(() => {
                        this.updateSnoozeStatusIfExpired()
                    }, diffInMilliSeconds)
                }
            } else {
                this.userProfile.snoozeStatus = false
                this.userProfile.snoozeExpiresAt = null
            }
        }
    }

    private isSnoozedForSetDuration() {
        return this.userProfile.snoozeExpiresAt && this.userProfile.snoozeStatus
    }

    clearTimer() {
        if (this.timerId) {
            clearTimeout(this.timerId)
            this.timerId = null
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
        this.clearTimer()
    }
}
